import React from 'react';
import Icon from '../ui/Icon';
import { timeAgo, formatWhen } from '../../utils/time';
import './NotificationCard.css';

// One notification / announcement visual language for the whole product (Notification Center AND Placement Hub).
// type + category -> icon and tone. Categories mirror the announcement categories:
// assessment · placement · deadline · company · general.
const CATEGORY_LABEL = { assessment: 'Assessment', placement: 'Placement', deadline: 'Deadline', company: 'Company', general: 'General' };
const CATEGORY_ICON = { assessment: 'test', placement: 'placement', deadline: 'clock', company: 'building', general: 'announcement' };
const TYPE_ICON = { assessment_assigned: 'test', assessment_scheduled: 'calendar', assessment_reminder: 'timer', result: 'chart' };
const TONE = { assessment: 'violet', placement: 'cyan', deadline: 'warning', company: 'indigo', general: 'neutral' };

export const categoryLabel = (c) => CATEGORY_LABEL[c] || 'General';

/**
 * @param {object}  n         a notification from GET /portal/notifications (or an announcement shaped the same way)
 * @param {boolean} expanded  show the full body and the action
 * @param {func}    onToggle  tap handler (marks read + expands)
 * @param {func}    onOpen    "Open" action
 */
export default function NotificationCard({ n, expanded = false, plain = false, onToggle, onOpen }) {
  const icon = TYPE_ICON[n.type] || CATEGORY_ICON[n.category] || 'notification';
  const tone = TONE[n.category] || 'neutral';
  const when = n.meta?.scheduled_at ? `Starts ${formatWhen(n.meta.scheduled_at)}` : null;
  const detail = n.type === 'result' && n.meta?.total ? `${n.meta.correct ?? 0} of ${n.meta.total} correct` : null;

  return (
    <div className={`z-notif z-notif--${tone}${plain ? ' z-notif--plain' : (n.read ? ' z-notif--read' : ' z-notif--unread')}${expanded ? ' z-notif--open' : ''}`}>
      <button type="button" className="z-notif-main" onClick={onToggle} aria-expanded={expanded}>
        <span className="z-notif-icon"><Icon name={icon} size={20} /></span>
        <span className="z-notif-body">
          <span className="z-notif-top">
            <span className="z-notif-title">{n.title}</span>
            {!plain && !n.read && <span className="z-notif-dot" role="img" aria-label="Unread" />}
          </span>
          <span className="z-notif-text">{n.body}</span>
          <span className="z-notif-meta">
            <span className="z-chip">{categoryLabel(n.category)}</span>
            {n.pinned && <span className="z-chip z-chip--violet"><Icon name="pin" size={12} /> Pinned</span>}
            {when && <span className="z-notif-when">{when}</span>}
            {detail && <span className="z-notif-when">{detail}</span>}
            <span className="z-notif-ago">{timeAgo(n.created_at)}</span>
          </span>
        </span>
      </button>
      {expanded && n.action && onOpen && (
        <div className="z-notif-actions">
          <button type="button" className="z-btn z-btn--secondary z-btn--sm" onClick={() => onOpen(n)}>
            Open <Icon name="arrow-right" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
